const mProductBuyButtonNormalizeMoney = (value) => {
  return String(value).replace(/(?:\s|&nbsp;)*kr\.(?=<|$)/gi, 'kr').replace(/(?:\s|&nbsp;)+kr(?=<|$)/gi, 'kr');
};

const mProductBuyButtonFormatMoneyFromTheme = (cents, currencyCode) => {
  const moneyFormat = window.theme?.moneyFormat;

  if (!moneyFormat) {
    return mProductBuyButtonNormalizeMoney(new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
    }).format((Number(cents) || 0) / 100));
  }

  const formatWithDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
    const fixedAmount = amount.toFixed(precision);
    const [dollars, remainder] = fixedAmount.split('.');

    return (
      dollars.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) +
      (precision > 0 && remainder ? decimal + remainder : '')
    );
  };

  const placeholderValues = {
    amount: formatWithDelimiters((Number(cents) || 0) / 100, 2),
    amount_no_decimals: formatWithDelimiters(Math.round((Number(cents) || 0) / 100), 0),
    amount_with_comma_separator: formatWithDelimiters((Number(cents) || 0) / 100, 2, '.', ','),
    amount_no_decimals_with_comma_separator: formatWithDelimiters(
      Math.round((Number(cents) || 0) / 100),
      0,
      '.',
      ',',
    ),
    amount_with_apostrophe_separator: formatWithDelimiters((Number(cents) || 0) / 100, 2, "'", '.'),
  };

  return mProductBuyButtonNormalizeMoney(moneyFormat.replace(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/g, (_, placeholder) => {
    return placeholderValues[placeholder] ?? placeholderValues.amount;
  }));
};

if (!customElements.get('m-product-buy-button')) {
  customElements.define(
    'm-product-buy-button',
    class MProductBuyButton extends HTMLElement {
      constructor() {
        super();
        this.currentVariant = null;
        this.isLoading = false;
        this.onSubmit = (event) => this.handleSubmit(event);
        this.onVariantChange = (event) => this.handleVariantChange(event);
      }

      connectedCallback() {
        this.formEl = this.querySelector('form');
        this.buttonEl = this.querySelector('[data-product-add-button]');
        this.buttonTextEl = this.querySelector('[data-product-add-button-text]');
        this.buttonLabelEl = this.querySelector('[data-product-add-button-label]');
        this.priceWrapEl = this.querySelector('[data-product-add-button-price-wrap]');
        this.priceEl = this.querySelector('[data-product-add-button-price]');
        this.errorEl = this.querySelector('[data-product-buy-error]');
        this.variantInputEl = this.querySelector('[data-product-variant-id]') || this.querySelector('[name="id"]');
        this.contextEl = this.getContextElement();
        this.currentVariant = this.getInitialVariant();

        this.formEl?.addEventListener('submit', this.onSubmit);
        this.contextEl?.addEventListener('variant:change', this.onVariantChange);
        this.render();
      }

      getContextElement() {
        return (
          this.closest('[data-product-variant-context]') ||
          this.closest('m-cart-upsell-item') ||
          this.closest('m-mini-product-card') ||
          this.closest('#main-product') ||
          this.parentElement
        );
      }

      getInitialVariant() {
        const variantId = Number(this.variantInputEl?.value || 0);
        if (!variantId) return null;

        return {
          id: variantId,
          available: !this.buttonEl?.disabled,
          price: Number(this.priceEl?.dataset.price || 0),
        };
      }

      handleVariantChange(event) {
        this.currentVariant = event.detail?.variant || null;
        this.updateVariantInput();
        this.hideError();
        this.render();
      }

      handleSubmit(event) {
        event.preventDefault();

        if (this.buttonEl?.disabled || this.isLoading) return;

        this.addToCart();
      }

      render() {
        if (!this.buttonEl) return;

        const hasVariant = Boolean(this.currentVariant?.id);
        const isAvailable = hasVariant && this.currentVariant.available !== false;

        this.buttonEl.disabled = this.isLoading || !isAvailable;
        this.buttonEl.classList.toggle('is-loading', this.isLoading);

        if (this.contextEl?.matches('m-cart-upsell-item')) {
          this.contextEl.classList.toggle('is-loading', this.isLoading);
        }

        this.updateButtonLabel(hasVariant, isAvailable);
        this.updatePrice(isAvailable);
      }

      updateButtonLabel(hasVariant, isAvailable) {
        if (!this.buttonLabelEl) return;

        const labels = this.buttonTextEl?.dataset || {};

        if (!hasVariant) {
          this.buttonLabelEl.textContent = labels.selectLabel || labels.addLabel || '';
          return;
        }

        this.buttonLabelEl.textContent = isAvailable ? labels.addLabel || '' : labels.soldOutLabel || '';
      }

      updatePrice(isAvailable) {
        if (!this.priceWrapEl || !this.priceEl) return;

        this.priceWrapEl.hidden = !isAvailable;

        if (!isAvailable || this.currentVariant?.price == null) return;

        this.priceEl.textContent = this.formatMoney(this.currentVariant.price);
      }

      updateVariantInput() {
        if (!this.variantInputEl) return;

        this.variantInputEl.value = this.currentVariant?.id || '';
      }

      async addToCart() {
        if (!this.currentVariant?.id || this.currentVariant.available === false || this.isLoading) return;

        this.isLoading = true;
        this.hideError();
        this.render();

        try {
          await window.CartActions.add(this.cartPayload, {
            source: this,
            variant: this.currentVariant,
            openDrawer: this.shouldOpenCartDrawer,
          });
        } catch (error) {
          this.showError(error.message || this.errorLabel);
        } finally {
          this.isLoading = false;
          this.render();
        }
      }

      get cartPayload() {
        const formData = this.formEl ? new FormData(this.formEl) : new FormData();

        formData.set('id', Number(this.currentVariant.id));

        if (!formData.get('quantity')) {
          formData.set('quantity', 1);
        }

        return formData;
      }

      get shouldOpenCartDrawer() {
        const upsellItem = this.closest('m-cart-upsell-item');

        return !(upsellItem && upsellItem.closest('[data-main-cart]'));
      }

      get errorLabel() {
        return this.errorEl?.dataset.errorLabel || 'Error';
      }

      showError(message) {
        if (!this.errorEl) {
          console.error('MProductBuyButton:', message);
          return;
        }

        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (!this.errorEl) return;

        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      formatMoney(cents) {
        const currencyCode = this.priceEl?.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';

        return mProductBuyButtonFormatMoneyFromTheme(cents, currencyCode);
      }

      disconnectedCallback() {
        this.formEl?.removeEventListener('submit', this.onSubmit);
        this.contextEl?.removeEventListener('variant:change', this.onVariantChange);
      }
    },
  );
}
